import {
  useDeletePlaidAccessTokenMutation,
  type FullPlaidAccessTokenFragment,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type DeletePlaidTokenModalUserProps = {
  token: FullPlaidAccessTokenFragment;
  onDeleted?: () => void;
};

export const DeletePlaidTokenModal = (
  props: DeletePlaidTokenModalUserProps & SystemProps,
) => {
  const {token, onRequestClose, onDeleted} = props;
  const [deleteToken, {loading}] = useDeletePlaidAccessTokenMutation();
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteToken({
        variables: {id: token._id},
      });
      onDeleted?.();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const accountNames = token.accounts.map((a) => a.name);

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Delete Plaid Token"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <div className="flex flex-col gap-3">
        <p>
          Are you sure you want to delete the token{' '}
          <strong className="font-mono text-sm">•••{token.tokenSuffix}</strong>?
        </p>
        {accountNames.length > 0 && (
          <div className="border-border rounded-md border p-3">
            <p className="text-muted-foreground mb-2 text-sm font-medium">
              Connected accounts that will be affected:
            </p>
            <ul className="text-muted-foreground list-inside list-disc text-sm">
              {accountNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-muted-foreground text-sm">
          This action cannot be undone. The connected accounts will no longer be
          able to sync transactions.
        </p>
      </div>
    </Modal>
  );
};
